import { withDefault } from "collection-utils";
import type { AppAnalyticsEvents } from "common/analytics/app-events";
import { logError, logInfo } from "common/debug-print";
import { getPersistentDeviceID } from "common/persistent-device-id";
import { onTrackingConsentChange, withTrackingConsent } from "common/tracking-consent";
import "firebase/analytics";
import firebase from "firebase/app";
import { getLocationSettings } from "lib/location";
import mixpanel from "mixpanel-browser";
import { localStorageGetItem } from "support/local-storage";
import { getAppFacilities } from "webapp/lib/app-renderer";
import { standalone } from "webapp/lib/device";
import { loadPageJavascript, removeQueryStrings } from "../lib/browser-hacks";
import { initFirebase } from "../lib/firebase";
import * as routes from "../routes";
import type { AnalyticsEvents } from "./events";
import { HubspotCustomEvents, MarketingEvents } from "./events";

// HubSpot API
type HubspotTracker = Array<
    | ["identify", { email: string }]
    | ["doNotTrack", { track: boolean }]
    | ["revokeCookieConsent"]
    | ["trackPageView"]
    | ["trackEvent", { id: string }]
>;

const partnerStackPublicKey = "pk_WjHdLw4wEWV2YzneXc7ghUUDUQ2Yu2FM";

const _hsq: HubspotTracker = ((window as any)._hsq = (window as any)._hsq ?? []);

function isProduction(): boolean {
    return process.env.NODE_ENV === "production";
}

interface AnalyticsConfig {
    app_name: string;
    app_id: string;
    app_user_id: string;
    device_id: string;
    standalone: boolean;
    screen_name: string | undefined;
}

let currentAppUserID: string | undefined;

// This assumes that we don't let go of an appUserID
// within the same memory context: the transition is
// unauthenticated -> authenticated exactly once, and
// anything else requires a full context reset
// (e.g. reloading the whole page.)
function setCurrentAppUserID(appUserID: string) {
    currentAppUserID = appUserID;
}

function configForPlayerAnalytics(): AnalyticsConfig {
    const appID = routes.appIdOrCustomDomain();
    const deviceID = getPersistentDeviceID(appID);
    const appUserID = currentAppUserID ?? `device-${deviceID}`;
    return {
        app_name: document.title,
        app_id: appID,
        app_user_id: appUserID,
        device_id: deviceID,
        standalone,
        screen_name: undefined,
    };
}

export function globalWindowGoogleAnalyticsMeasureIDs(): string[] {
    return withDefault((window as any).additionalGAMeasureIDs, []);
}

export function appendGlobalWindowGoogleAnalyticsMeasureID(measureID: string): void {
    const addtlMeasureIDs = globalWindowGoogleAnalyticsMeasureIDs();
    if (addtlMeasureIDs.indexOf(measureID) < 0) {
        addtlMeasureIDs.push(measureID);
    }
    (window as any).additionalGAMeasureIDs = addtlMeasureIDs;
}

export function upsertGoogleAnalyticsMeasureID(
    measureID: string,
    configParams: AnalyticsConfig = configForPlayerAnalytics()
) {
    gtag("config", measureID, configParams);
}

function initializeHubspot() {
    const hubspotAccountID = getLocationSettings().hubspotAccountID;
    const hubspotScript = () => loadPageJavascript(`//js.hs-scripts.com/${hubspotAccountID}.js`);

    window.addEventListener("load", hubspotScript, false);

    // Make sure we don't track the user if they don't consent.
    onTrackingConsentChange((consent: boolean) => {
        _hsq.push(["doNotTrack", { track: consent }]);
    });
}

function initializeMixpanel() {
    mixpanel.init("4b0e7a406c99710357a1b6aad2d9ad9f");

    // Read Mixpanel distinct_id for users coming from marketing site
    const url = new URL(window.location.href);
    const mixpanelDistinctId = url.searchParams.get("mdid");
    if (mixpanelDistinctId !== null) {
        mixpanel.identify(mixpanelDistinctId);
        removeQueryStrings("mdid");
    }
}

/**
 * Adapted from PW's script:

        <script id="profitwell-js" data-pw-auth="0f543b3ce6fded4a25b24d66ec84dbb0">
            (function (i, s, o, g, r, a, m) {
                i[o] =
                    i[o] ||
                    function () {
                        (i[o].q = i[o].q || []).push(arguments);
                    };
                a = s.createElement(g);
                m = s.getElementsByTagName(g)[0];
                a.async = 1;
                a.src = r + "?auth=" + s.getElementById(o + "-js").getAttribute("data-pw-auth");
                m.parentNode.insertBefore(a, m);
            })(window, document, "profitwell", "script", "https://public.profitwell.com/js/profitwell.js");

            profitwell("start", {});
        </script>

 */
function initializeProfitwell() {
    const anywindow = window as any;

    anywindow.profitwell =
        anywindow.profitwell ??
        function (...args: any[]) {
            (anywindow.profitwell.q = anywindow.profitwell.q ?? []).push(...args);
        };

    const pwScript = document.createElement("script");
    pwScript.async = true;
    pwScript.src = "https://public.profitwell.com/js/profitwell.js?auth=0f543b3ce6fded4a25b24d66ec84dbb0";
    const prevScript = document.getElementsByTagName("script")[0];
    prevScript.parentNode?.insertBefore(pwScript, prevScript);

    anywindow.profitwell("start", {});
}

function initializePartnerStack() {
    const anyWindow = window as any;

    anyWindow.growsumo =
        anyWindow.growsumo ??
        function (...args: any[]) {
            (anyWindow.growsumo.q = anyWindow.growsumo.q ?? []).push(...args);
        };

    const psScript = document.createElement("script");
    psScript.async = true;
    psScript.src = "https://js.partnerstack.com/v1/";
    psScript.type = "text/javascript";

    psScript.onload = anyWindow.onreadystatechange = function () {
        const rs = anyWindow.growsumo.readyState;
        if (rs !== "complete" && rs !== "loaded") return;
        try {
            anyWindow.growsumo._initialize(partnerStackPublicKey);
            if (typeof anyWindow.growsumoInit === "function") {
                anyWindow.growsumo.growsumoInit();
            }
        } catch (e: unknown) {
            logError("Unable to initialize growSumoInit", e);
        }
    };

    const prevScript = document.getElementsByTagName("script")[0];
    prevScript.parentNode?.insertBefore(psScript, prevScript);
}

//
const xMarketingMeasure = "GTM-5MXCZVT";
const gtmJS = `https://www.googletagmanager.com/gtm.js?id=${xMarketingMeasure}&l=gtmLayer`;
let loadedGtmJS = false;

function initializeGoogleTagManager() {
    if (loadedGtmJS) return;

    loadPageJavascript(gtmJS);
    loadedGtmJS = true;
}

export async function initializeAnalytics() {
    if (routes.isPlayer()) {
        // Elide all analytics in the player. We only want to support
        // custom Google Analytics properties here.
        const configParams = configForPlayerAnalytics();
        for (const measure of globalWindowGoogleAnalyticsMeasureIDs()) {
            upsertGoogleAnalyticsMeasureID(measure, configParams);
        }
    } else {
        initializeProfitwell();
        // We don't want to mix Google Tag Manager and Firebase Analytics
        // events, so we'll give GTM its own data layer.
        (window as any).gtmLayer = (window as any).gtmLayer ?? [{ "gtm.start": new Date().getTime(), event: "gtm.js" }];

        // We are only doing this for side effects. Firebase Analytics is now loaded.
        // In the builder, we load Hubspot and mixpanel.
        withTrackingConsent(() => {
            getFirebaseAnalyticsInstance();
            initializeGoogleTagManager();
            initializePartnerStack();

            // We have a sandbox account for hubspot
            // So we don't need to be in prod
            initializeHubspot();
            if (isProduction()) {
                initializeMixpanel();
            }
        });
    }
}

// Event names can be up to 40 characters long, may only contain alphanumeric
// characters and underscores ("_"), and must start with an alphabetic character.
// The "firebase_", "google_", and "ga_" prefixes are reserved and should not be used.
function sanitizeEventNameForFirebase(event: string): string {
    return event.replace(/ /g, "_").slice(0, 40);
}

let globalFirebaseAnalyticsInstance: firebase.analytics.Analytics | undefined;

function getFirebaseAnalyticsInstance(): firebase.analytics.Analytics {
    if (globalFirebaseAnalyticsInstance !== undefined) return globalFirebaseAnalyticsInstance;

    initFirebase();
    globalFirebaseAnalyticsInstance = firebase.analytics();
    return globalFirebaseAnalyticsInstance;
}

type IdentifyUserInfo = {
    email?: string;
    name?: string;
    avatar?: string;

    // We can send other properties with identify calls
    [propertyName: string]: any;
};

export function builderDocURLWithCampaign(urlAsString: string): string {
    const url = new URL(urlAsString);
    url.searchParams.set("utm_source", "builder");
    url.searchParams.set("utm_medium", "builder_info_link");
    url.searchParams.set("utm_campaign", "BuilderDocLinks");
    url.searchParams.set("utm_content", "publish");
    return url.href;
}

function identifyInHubspot(userEmail: string, properties: Record<string, any> = {}) {
    _hsq.push([
        "identify",
        {
            email: userEmail,
            ...properties,
        },
    ]);

    // Track a page view so we actually identify this user
    _hsq.push(["trackPageView"]);
}

function identifyInMixPanel(userID: string, properties: { [key: string]: any } = {}) {
    // According to the latest Mixpanel docs, we are only
    // supposed to call identify again with new id, instead of alias.
    mixpanel.identify(userID);

    // https://help.mixpanel.com/hc/en-us/articles/115004708186-Profile-Properties
    const mixpanelProperties: { [key: string]: any } = {
        ...properties,
        // We could likely assume this but it makes reporting easier
        authenticated: true,
    };

    // We do not want to send PII to Mixpanel
    ["avatar", "email", "name"].forEach(prop => {
        if (prop in properties) {
            delete mixpanelProperties[prop];
        }
    });

    mixpanel.people.set(mixpanelProperties);
}

export async function identifyUser(userID: string, userInfo: IdentifyUserInfo = {}): Promise<void> {
    // This code path was never used by the player, so it
    // really shouldn't ever do anything there.
    if (routes.isPlayer()) return;
    withTrackingConsent(() => {
        getFirebaseAnalyticsInstance().setUserId(userID);
        // This is for Google Tag Manager; the Firebase user ID is now available
        // as a Data Layer Variable.
        (window as any).gtmLayer?.push({ userID });

        if (userInfo.email !== undefined) {
            identifyInHubspot(userInfo.email);
        }
    });

    if (isProduction()) {
        withTrackingConsent(() => {
            identifyInMixPanel(userID, userInfo);
        });
    }
}

// replace all functional pieces with firebase analytics and auth
function trackEventInFirebaseAnalytics(name: string, properties: object) {
    getFirebaseAnalyticsInstance().logEvent(sanitizeEventNameForFirebase(name), properties);
}

function trackEventInMixPanel<Name extends keyof AnalyticsEvents>(event: Name, properties: AnalyticsEvents[Name]) {
    mixpanel.track(event, properties);
}

// Difference between this one and the trackEventInHubspot is that this one uses the new API that allows us to send the properties
export async function trackCustomEventInHubspot<Name extends keyof AnalyticsEvents>(
    eventName: Name,
    properties: AnalyticsEvents[Name]
) {
    const event = eventName.replace(/ /g, "_");
    const validatedEventName = isProduction() ? `pe19936848_${event}` : `pe20075072_${event}`;
    await getAppFacilities().callAuthCloudFunction("sendHubspotCustomEvent", {
        eventName: validatedEventName,
        properties,
    });
}

function trackEventInHubspot<Name extends keyof AnalyticsEvents>(event: Name, options: AnalyticsEvents[Name]): void {
    if (!MarketingEvents.includes(event as any)) {
        return;
    }

    if (HubspotCustomEvents.includes(event as any)) {
        void trackCustomEventInHubspot(event, options);
    }

    _hsq.push(["trackEvent", { id: event }]);
}

function trackEventInGTM<Name extends keyof AnalyticsEvents>(event: Name, properties: AnalyticsEvents[Name]) {
    (window as any).gtmLayer?.push({ ...properties, event: sanitizeEventNameForFirebase(event) });
}

export async function trackCompletedSignUpFlow(email: string): Promise<void> {
    const hubspotAccountID = getLocationSettings().hubspotAccountID;

    const baseUrl = new URL("https://track.hubspot.com/v1/event");
    baseUrl.searchParams.set("_n", "completed sign up HTTP");
    baseUrl.searchParams.set("_a", hubspotAccountID);
    baseUrl.searchParams.set("email", email);

    try {
        await fetch(baseUrl.toString(), {
            mode: "no-cors",
        });
    } catch (err: unknown) {
        logError(err);
    }
}

export function trackEvent<Name extends keyof AnalyticsEvents>(event: Name, options: AnalyticsEvents[Name]): void {
    const prod = isProduction();
    if (!prod) {
        logInfo("ANALYTICS", event, options);
    }

    if (routes.isPlayer()) {
        // Note that we don't use Firebase Analytics in the player.
        // Instead we'll try to send to the user-configured analytics IDs.
        gtag("event", event, {
            ...options,
            send_to: globalWindowGoogleAnalyticsMeasureIDs(),
        });
    } else {
        withTrackingConsent(() => {
            trackEventInFirebaseAnalytics(event, options);
            trackEventInGTM(event, options);

            // We have a sandbox account for hubspot
            // So we don't need to be in prod
            trackEventInHubspot(event, options);
            if (prod) {
                trackEventInMixPanel(event, options);
            }
        });
    }
}

export function trackPushScreen(appID: string, title?: string): void {
    // We only want to track these pushScreen events in the player.
    if (!routes.isPlayer()) return;

    const configParams: AnalyticsConfig = { ...configForPlayerAnalytics(), screen_name: title ?? "" };
    for (const measure of globalWindowGoogleAnalyticsMeasureIDs()) {
        upsertGoogleAnalyticsMeasureID(measure, configParams);
    }

    trackEvent("screen_view", { app_id: appID, screen_name: title ?? "" });
}

export function trackAppLogin(appID: string, appUserID: string): void {
    if (!routes.isPlayer()) return;

    setCurrentAppUserID(appUserID);
    trackEvent("login", { app_id: appID, app_user_id: appUserID });
}

export function trackPlayerEvent<Name extends keyof AppAnalyticsEvents>(
    event: Name,
    options: AppAnalyticsEvents[Name]
): void {
    if (!routes.isPlayer()) return;

    return trackEvent(event, options as unknown as any);
}

export function trackCustomPlayerEvent<Name extends keyof AppAnalyticsEvents>(
    event: Name,
    options: AppAnalyticsEvents[Name]
): void {
    if (globalWindowGoogleAnalyticsMeasureIDs().length === 0) return;

    return trackPlayerEvent(event, options);
}

export function partnerStackSignUp(name: string, email: string, user_id: string) {
    const partnerKey = localStorageGetItem("partner_key");
    try {
        if (partnerKey === undefined) return;
        const anyWindow = window as any;
        const growSumo = (anyWindow.growsumo =
            anyWindow.growsumo ??
            function (...args: any[]) {
                (anyWindow.growsumo.q = anyWindow.growsumo.q ?? []).push(...args);
            });

        if (growSumo !== undefined) {
            const decodedPartnerKey = atob(partnerKey);

            growSumo.data.name = name;
            growSumo.data.email = email;
            growSumo.data.customer_key = user_id;
            // Requires partner_key in order for it to be seen in referrals
            growSumo.data.partner_key = decodedPartnerKey;

            growSumo.data.public_key = partnerStackPublicKey;

            growSumo.createSignup();
        }
    } catch (err: unknown) {
        logError(err);
    }
}
